/**
 * assets/product-page.js
 * Sin frameworks. Funciona por sección (soporta varias instancias / theme editor).
 */
class SaulceveProduct extends HTMLElement {
  constructor() {
    super();
    this.productData = JSON.parse(this.querySelector('[data-product-json]').textContent);
    this.form = this.querySelector('form.shopify-product-form');
    this.variantIdInput = this.querySelector('input[name="id"]');
    this.qtyInput = this.querySelector('.spc-qty input');
    this.addBtn = this.querySelector('[data-add-to-cart]');
    this.priceCurrent = this.querySelector('[data-price-current]');
    this.priceCompare = this.querySelector('[data-price-compare]');
    this.priceSave = this.querySelector('[data-price-save]');
    this.stockEl = this.querySelector('[data-stock]');
    this.mobilePrice = this.querySelector('[data-mobile-price]');

    this.selectedOptions = this.productData.selectedOptions || [];

    this.bindGallery();
    this.bindOptions();
    this.bindQty();
    this.bindAccordion();
    this.bindDescriptionToggle();
    this.bindSecondaryDescriptionToggle();
    this.bindAddToCart();
  }

  /* ---------- Gallery ---------- */
  bindGallery() {
    const thumbs = this.querySelectorAll('.spc-gallery__thumb');
    const slides = this.querySelectorAll('.spc-gallery__slide');
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const target = thumb.dataset.mediaId;
        thumbs.forEach((t) => t.classList.toggle('is-active', t === thumb));
        slides.forEach((s) => s.classList.toggle('is-active', s.dataset.mediaId === target));
      });
    });
  }

  showMediaForVariant(variant) {
    if (!variant || !variant.featured_media) return;
    const slide = this.querySelector(`.spc-gallery__slide[data-media-id="${variant.featured_media.id}"]`);
    const thumb = this.querySelector(`.spc-gallery__thumb[data-media-id="${variant.featured_media.id}"]`);
    if (slide) {
      this.querySelectorAll('.spc-gallery__slide').forEach((s) => s.classList.remove('is-active'));
      slide.classList.add('is-active');
    }
    if (thumb) {
      this.querySelectorAll('.spc-gallery__thumb').forEach((t) => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    }
  }

  /* ---------- Options / variants ---------- */
  bindOptions() {
    const swatches = this.querySelectorAll('[data-option-value]');
    swatches.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.hasAttribute('disabled')) return;
        const position = parseInt(btn.dataset.optionPosition, 10);
        this.selectedOptions[position - 1] = btn.dataset.optionValue;

        this.querySelectorAll(`[data-option-position="${position}"]`).forEach((b) => {
          b.classList.toggle('is-selected', b === btn);
        });

        this.updateLabel(position, btn.dataset.optionValue);
        this.updateVariant();
      });
    });
  }

  updateLabel(position, value) {
    const label = this.querySelector(`[data-option-label="${position}"]`);
    if (label) label.textContent = value;
  }

  findVariant() {
    return this.productData.variants.find((v) =>
      v.options.every((opt, i) => opt === this.selectedOptions[i])
    );
  }

  updateVariant() {
    const variant = this.findVariant();
    const allSwatches = this.querySelectorAll('[data-option-value]');

    if (!variant) {
      allSwatches.forEach((b) => (b.disabled = false));
      if (this.addBtn) {
        this.addBtn.disabled = true;
        this.addBtn.querySelector('[data-btn-label]').textContent = 'No disponible';
      }
      return;
    }

    if (this.variantIdInput) this.variantIdInput.value = variant.id;
    this.updatePrice(variant);
    this.updateStock(variant);
    this.showMediaForVariant(variant);
    this.updateURL(variant);
    this.updateAvailableOptions(variant);

    if (this.addBtn) {
      const label = this.addBtn.querySelector('[data-btn-label]');
      if (!variant.available) {
        this.addBtn.disabled = true;
        label.textContent = 'Agotado';
      } else {
        this.addBtn.disabled = false;
        label.textContent = 'Añadir a la cesta';
      }
    }
  }

  updateAvailableOptions(currentVariant) {
    // Deshabilita combinaciones sin stock manteniendo el resto seleccionable
    const positions = this.productData.optionsCount;
    for (let pos = 1; pos <= positions; pos++) {
      this.querySelectorAll(`[data-option-position="${pos}"]`).forEach((btn) => {
        const testOptions = [...this.selectedOptions];
        testOptions[pos - 1] = btn.dataset.optionValue;
        const match = this.productData.variants.find((v) =>
          v.options.every((opt, i) => opt === testOptions[i])
        );
        btn.disabled = !match || !match.available;
      });
    }
  }

  formatMoney(cents) {
    const amount = cents / 100;
    const format = this.productData.currencyFormat || '€{{amount_with_comma_separator}}';

    const withDot2 = amount.toFixed(2); // 1234.56
    const withCommaSeparator = amount.toLocaleString('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }); // 1.234,56
    const noDecimals = Math.round(amount).toLocaleString('es-ES'); // 1.234
    const noDecimalsPlain = String(Math.round(amount)); // 1234
    const withApostrophe = withDot2.replace(/\B(?=(\d{3})+(?!\d))/g, "'"); // 1'234.56

    return format
      .replace('{{amount_with_comma_separator}}', withCommaSeparator)
      .replace('{{amount_no_decimals_with_comma_separator}}', noDecimals)
      .replace('{{amount_no_decimals}}', noDecimalsPlain)
      .replace('{{amount_with_apostrophe_separator}}', withApostrophe)
      .replace('{{amount}}', withDot2);
  }

  updatePrice(variant) {
    const format = (cents) => this.formatMoney(cents);
    if (this.priceCurrent) this.priceCurrent.textContent = format(variant.price);
    if (this.mobilePrice) this.mobilePrice.textContent = format(variant.price);

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      if (this.priceCompare) {
        this.priceCompare.textContent = format(variant.compare_at_price);
        this.priceCompare.hidden = false;
      }
      if (this.priceSave) {
        const pct = Math.round(100 - (variant.price / variant.compare_at_price) * 100);
        this.priceSave.textContent = `-${pct}%`;
        this.priceSave.hidden = false;
      }
    } else {
      if (this.priceCompare) this.priceCompare.hidden = true;
      if (this.priceSave) this.priceSave.hidden = true;
    }
  }

  updateStock(variant) {
    if (!this.stockEl) return;
    const textEl = this.stockEl.querySelector('span:not(.spc-stock__dot)');
    if (!textEl) return;
    this.stockEl.classList.remove('spc-stock--low', 'spc-stock--out');
    if (!variant.available) {
      this.stockEl.classList.add('spc-stock--out');
      textEl.textContent = 'Sin stock actualmente';
    } else if (variant.inventory_quantity !== null && variant.inventory_quantity <= 5 && variant.inventory_quantity > 0) {
      this.stockEl.classList.add('spc-stock--low');
      textEl.textContent = `¡Solo quedan ${variant.inventory_quantity} unidades!`;
    } else {
      textEl.textContent = 'Hecho bajo pedido para ti';
    }
  }

  updateURL(variant) {
    if (!history.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url);
  }

  /* ---------- Quantity ---------- */
  bindQty() {
    this.querySelectorAll('[data-qty-decrease]').forEach((btn) =>
      btn.addEventListener('click', () => {
        const val = Math.max(1, parseInt(this.qtyInput.value, 10) - 1);
        this.qtyInput.value = val;
      })
    );
    this.querySelectorAll('[data-qty-increase]').forEach((btn) =>
      btn.addEventListener('click', () => {
        const val = parseInt(this.qtyInput.value, 10) + 1;
        this.qtyInput.value = val;
      })
    );
  }

  /* ---------- Accordion ---------- */
  bindAccordion() {
    this.querySelectorAll('.spc-accordion__trigger').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const item = trigger.closest('.spc-accordion__item');
        const wasOpen = item.classList.contains('is-open');
        item.parentElement.querySelectorAll('.spc-accordion__item').forEach((i) => i.classList.remove('is-open'));
        if (!wasOpen) item.classList.add('is-open');
      });
    });
  }

  bindDescriptionToggle() {
    const description = this.querySelector('[data-mobile-description]');
    const toggle = this.querySelector('[data-description-toggle]');
    if (!description || !toggle) return;

    const reset = () => {
      description.classList.remove('is-expanded');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.textContent = toggle.dataset.expandLabel;
      toggle.hidden = description.scrollHeight <= description.clientHeight + 1;
    };

    toggle.addEventListener('click', () => {
      const expanded = description.classList.toggle('is-expanded');
      toggle.setAttribute('aria-expanded', String(expanded));
      toggle.textContent = expanded ? toggle.dataset.collapseLabel : toggle.dataset.expandLabel;
    });

    window.addEventListener('resize', reset);
    requestAnimationFrame(reset);
  }

  bindSecondaryDescriptionToggle() {
    const description = this.querySelector('[data-secondary-description]');
    const toggle = this.querySelector('[data-secondary-description-toggle]');
    if (!description || !toggle) return;

    toggle.addEventListener('click', () => {
      const expanded = description.classList.toggle('is-expanded');
      toggle.setAttribute('aria-expanded', String(expanded));
      toggle.textContent = expanded ? toggle.dataset.collapseLabel : toggle.dataset.expandLabel;

      const panel = toggle.closest('.spc-accordion__panel');
      if (panel && panel.closest('.spc-accordion__item')?.classList.contains('is-open')) {
        panel.style.maxHeight = `${panel.scrollHeight}px`;
      }
    });
  }

  /* ---------- Add to cart (AJAX) ---------- */
  bindAddToCart() {
    if (!this.form || !this.addBtn) return;
    this.addBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      if (this.addBtn.disabled) return;

      const label = this.addBtn.querySelector('[data-btn-label]');
      const originalLabel = label.textContent;
      this.addBtn.disabled = true;
      label.textContent = 'Añadiendo…';

      try {
        const formData = new FormData(this.form);
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error('cart_add_failed');

        label.textContent = 'Añadido ✓';
        document.dispatchEvent(new CustomEvent('cart:updated'));
        // Si el tema tiene un drawer de carrito, abrirlo:
        document.dispatchEvent(new CustomEvent('cart:open'));

        setTimeout(() => {
          label.textContent = originalLabel;
          this.addBtn.disabled = false;
        }, 1800);
      } catch (err) {
        label.textContent = 'Error, reintenta';
        setTimeout(() => {
          label.textContent = originalLabel;
          this.addBtn.disabled = false;
        }, 1800);
      }
    });
  }
}

customElements.define('saulceve-product', SaulceveProduct);
